import { useEffect, useRef } from "react";
import { useAppContext } from "./useAppContext";
import { useConfigs } from "./useConfigs";
import type { Metric } from "web-vitals";

/** Log resource that are large or take long to load, and monitor web vitals */
export function usePerformanceMonitoring() {
	const { logger, serverLogger, context, editor } = useAppContext();

	/**
	 * - undefined: no yet initialized
	 * - true/false: should load and log or not
	 * - Prevent double initialization in React StrictMode/development
	 */
	const logWebVitalsRef = useRef<boolean | undefined>(undefined);

	const [
		[logWebVitalPercent = "1", logFetchLargerThanMb = "5", logFetchSlowerThanSeconds = "20"],
		isPendingConfigs,
	] = useConfigs([
		{
			key: "Mobile.Performance.LogWebVitalPercent",
		},
		{
			key: "Mobile.Performance.LogFetchLargerThanMb",
		},
		{
			key: "Mobile.Performance.LogFetchSlowerThanSeconds",
		},
	]);

	// Initialize web-vitals monitoring (prevent double initialization in StrictMode)
	useEffect(() => {
		if (logWebVitalsRef.current === false) return;
		if (isPendingConfigs) return;

		if (logWebVitalsRef.current === undefined) {
			// Set once
			if (logWebVitalPercent !== undefined) {
				logWebVitalsRef.current = Math.random() * 100 < parseFloat(logWebVitalPercent);
			}
		}

		// Skip if already initialized to prevent double logging in React StrictMode
		if (!logWebVitalsRef.current) return;

		const logWebVital = (metric: Metric) => {
			try {
				const { name, value, rating, delta, id, navigationType } = metric;

				const logLevel: eXpress.core.LogLevel =
					rating === "poor"
						? eXpress.core.LogLevel.Warning
						: eXpress.core.LogLevel.Information;

				const logCode = `creator-webvital-${name?.toLowerCase()}` as const;
				const message = `${logCode}: Web Vital ${name}: ${Math.round(value)}ms (${rating})`;
				logger.warn(message);

				serverLogger.log({
					logLevel,
					logCode,
					message,
					value: Math.round(value),
					rating,
					delta: Math.round(delta),
					id,
					navigationType,
				});
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : String(err);
				logger.warn(`Failed to log web vital ${metric.name}: ${errorMessage}`);
			}
		};

		// Initialize web-vitals using dynamic import
		const initializeWebVitals = async () => {
			try {
				// Mark as initialized before starting to prevent race conditions
				logWebVitalsRef.current = true;

				// Dynamically import web-vitals
				const webVitals = await import(
					/* webpackChunkName: "express-web-vitals" */
					"web-vitals"
				);

				// Monitor Core Web Vitals
				webVitals.onCLS?.(logWebVital);
				webVitals.onFCP?.(logWebVital);
				webVitals.onINP?.(logWebVital);
				webVitals.onLCP?.(logWebVital);
				webVitals.onTTFB?.(logWebVital);

				logger.info("Web Vitals monitoring enabled");
			} catch (err) {
				// Reset flag on error so it can be retried
				logWebVitalsRef.current = false;
				const errorMessage = err instanceof Error ? err.message : String(err);
				logger.warn(`Failed to initialize web-vitals: ${errorMessage}`);
			}
		};

		// Run the async initialization (void to indicate intentionally not awaited)
		void initializeWebVitals();
	}, [context, editor, isPendingConfigs, logWebVitalPercent, logger, serverLogger]);

	useEffect(() => {
		// https://web.dev/custom-metrics/#resource-timing-api
		// Catch errors since some browsers throw when using the new `type` option.
		// https://bugs.webkit.org/show_bug.cgi?id=209216
		let po: PerformanceObserver | undefined;
		try {
			po = new PerformanceObserver((list) => {
				for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
					// Skip checks on our javascript sizes
					if (entry.name.endsWith(".js")) return;

					if (
						logFetchLargerThanMb &&
						parseFloat(logFetchLargerThanMb) * 1024 * 1024 < entry.transferSize
					) {
						const logCode = `creator-large-fetch`;
						const message = `${logCode}: Performance warning: ${entry.name}: ${
							Math.round(entry.transferSize / 1024 / 102.4) / 10
						}MB`;
						logger.warn(message);

						serverLogger.log({
							logLevel: eXpress.core.LogLevel.Warning,
							logCode,
							message,
							value: entry.transferSize,
							initiatorType: entry.initiatorType,
							name: entry.name,
						});
					}

					if (
						logFetchSlowerThanSeconds &&
						entry.duration > parseFloat(logFetchSlowerThanSeconds) * 1000
					) {
						const size =
							entry.transferSize === 0
								? ", from cache"
								: `, ${Math.ceil(entry.transferSize / 1024)}KB`;
						const initiatorType = entry.initiatorType
							? `, initiatorType: ${entry.initiatorType}`
							: "";

						const logCode = `creator-slow-fetch`;
						const message = `${logCode}: Performance warning: ${
							entry.name
						}: Took ${Math.ceil(entry.duration)}ms to load${size}${initiatorType}`;
						logger.warn(message);

						serverLogger.log({
							logLevel: eXpress.core.LogLevel.Warning,
							logCode,
							message,
							value: entry.duration,
							minLogValue: parseFloat(logFetchSlowerThanSeconds) * 1000,
							initiatorType: entry.initiatorType,
							name: entry.name,
						});
					}
				}
			});
			po.observe({ type: "resource", buffered: true });
		} catch (_e) {
			// Do nothing if the browser doesn't support this API.
		}

		return () => {
			po?.disconnect();
		};
	}, [
		context.account.id,
		editor,
		logFetchLargerThanMb,
		logFetchSlowerThanSeconds,
		logger,
		serverLogger,
	]);
}
